import { lazy, startTransition, Suspense, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { AppRouteBody } from '@/components/AppRouteBody';
import { TabOption, Tabs } from '@/components/chariot-ui';
import { FeatureLock } from '@/components/legacy-ui/FeatureLock';
import { createContactSupportButton } from '@/components/legacy-ui/utils';
import { Spinner } from '@/components/ui/spinner';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useTablePrefetch } from '@/hooks/useTablePrefetch';
import { AIAssistant } from '@/sections/insights/aiAssistant';
import GraphExplorer from '@/sections/insights/graphExplorer';
import QueryBuilder from '@/sections/insights/queryBuilder';

// Lazy load Metrics for code splitting (prevents module load until tab clicked)
const Metrics = lazy(() => import('./metrics'));
const BenchmarksTab = lazy(() => import('./benchmarks/BenchmarksTab'));

export const Insights = () => {
  // Prefetch primary table data so Assets/Vulnerabilities/Technologies load instantly
  useTablePrefetch();

  const { canConverse, isLoading: entitlementsLoading } = useEntitlements();

  const insightTabs: TabOption[] = [
    ...(canConverse ? [{ label: 'AI Assistant', value: 'ai-assistant' }] : []),
    { label: 'Metrics', value: 'metrics' },
    { label: 'Benchmarks', value: 'benchmarks' },
    { label: 'Query Builder', value: 'query-builder' },
    { label: 'Graph Explorer', value: 'graph-explorer' },
  ];

  const [searchParams, setSearchParams] = useSearchParams();
  const {
    locked: aiAssistantLocked,
    lockTitle: aiAssistantLockTitle,
    lockDescription: aiAssistantLockDescription,
    featureName: aiAssistantFeatureName,
    isLoading: aiAssistantLoading,
  } = useFeatureLock('aiAssistant');
  const validTabs = insightTabs.map(tab => tab.value);
  const tabFromUrl = searchParams.get('tab');
  const queryState = searchParams.get('state'); // Check for query builder state

  // Determine initial tab - prioritize explicit tab, then infer from query state
  const getInitialTab = () => {
    if (validTabs.includes(tabFromUrl || '')) {
      return tabFromUrl;
    }
    // If no explicit tab but there's query state, default to query-builder
    if (queryState) {
      return 'query-builder';
    }
    // Default to AI Assistant if available, otherwise Metrics
    return canConverse ? 'ai-assistant' : 'metrics';
  };

  const initialTab = getInitialTab();

  // Derive tab directly from URL (single source of truth, no state sync needed)
  const selectedTabValue = useMemo(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && validTabs.includes(urlTab)) {
      return urlTab;
    }
    return initialTab || (canConverse ? 'ai-assistant' : 'metrics');
  }, [searchParams, validTabs, initialTab, canConverse]);

  // Custom setter that updates URL (which automatically updates selectedTabValue via useMemo)
  const setSelectedTab = (newTab: string) => {
    if (validTabs.includes(newTab)) {
      // Use startTransition for non-urgent tab content loading
      // This keeps the tab button responsive while Dashboard loads in background
      startTransition(() => {
        setSearchParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('tab', newTab);
            return newParams;
          },
          { replace: true }
        );
      });
    }
  };

  const selectedTab: TabOption | null =
    insightTabs.find(tab => tab.value === selectedTabValue) || null;

  const handleTabSelect = (tab: TabOption) => {
    const targetValue = tab.value as string;

    if (
      selectedTabValue === 'query-builder' &&
      targetValue !== 'query-builder'
    ) {
      setSelectedTab(targetValue);
    } else {
      setSelectedTab(targetValue);
    }
  };

  const renderContent = () => {
    if (entitlementsLoading) {
      return <Spinner centered />;
    }

    switch (selectedTabValue) {
      case 'ai-assistant':
        if (aiAssistantLoading) {
          return <Spinner centered />;
        }
        return (
          <div className="relative size-full">
            <FeatureLock
              locked={aiAssistantLocked}
              title={aiAssistantLockTitle}
              description={aiAssistantLockDescription}
              action={createContactSupportButton(aiAssistantFeatureName)}
            >
              <AIAssistant />
            </FeatureLock>
          </div>
        );
      case 'query-builder':
        return <QueryBuilder />;
      case 'graph-explorer':
        return <GraphExplorer />;
      case 'benchmarks':
        return (
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <div className="text-chariot-text-secondary mb-2 text-center">
                  Loading benchmarks...
                </div>
              </div>
            }
          >
            <BenchmarksTab />
          </Suspense>
        );
      case 'metrics':
        return (
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <div className="text-chariot-text-secondary mb-2">
                    Loading metrics...
                  </div>
                </div>
              </div>
            }
          >
            <Metrics />
          </Suspense>
        );
      default:
        return (
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <div className="text-chariot-text-secondary mb-2">
                    Loading metrics...
                  </div>
                </div>
              </div>
            }
          >
            <Metrics />
          </Suspense>
        );
    }
  };

  return (
    <AppRouteBody title="Insights" data-tutorial="insights-page">
      <div className="flex h-full flex-col overflow-hidden">
        <Tabs
          tabs={insightTabs}
          selected={selectedTab}
          onSelect={handleTabSelect}
          variant="primary"
        />
        <div
          className={`mt-4 h-full pb-6 ${selectedTabValue !== 'graph-explorer' ? 'overflow-y-auto' : 'overflow-hidden'}`}
        >
          {renderContent()}
        </div>
      </div>
    </AppRouteBody>
  );
};
